import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import '../assets/css/contact.css';
import '../assets/css/psearch.css';
import SignInModal from './SignInModal';
import Nav from './Nav';
import SEO from './SEO';

function Contact() {
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Error signing in:', error.message);
        setAuthMessage(`Sign in error: ${error.message}`);
      } else {
        console.log('OAuth initiated:', data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setAuthMessage(`Unexpected error: ${err.message}`);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserDropdown(false);
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    message: '',
    bugReport: '',
    productRequest: '',
    generalQuery: '',
    anonymousQuery: ''
  });
  
  const [activeForm, setActiveForm] = useState('anonymousQuery');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        category: formData.category || '',
        message: formData.message || '',
        timestamp: new Date().toISOString()
      };

      await fetch(process.env.REACT_APP_CONTACT_FORM_SCRIPT_URL, {
        method: "POST",
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      setModalMessage('Message submitted successfully!');
      setShowModal(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: '',
        message: '',
        bugReport: '',
        productRequest: '',
        generalQuery: '',
        anonymousQuery: ''
      });
      
      // Auto-hide the modal after 3 seconds
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setModalMessage('An error occurred. Please try again.');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  

  const toggleForm = (formName) => {
    setActiveForm(activeForm === formName ? null : formName);
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with Yemberzal. Reach out for brand partnerships, collaborations, or questions about Kashmiri fashion. We'd love to hear from you."
        path="/contact"
      />
    <div className="container contact-shell">
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>{modalMessage}</p>
            <button onClick={() => setShowModal(false)}>OK</button>
          </div>
        </div>
      )}
      <Nav />
      
      <section className="contact-hero">
        <div className="contact-hero-inner">
          <div className="page-hero-fullwidth" style={{ 
            backgroundColor: '#f5f5f5', 
            paddingTop: '40px',
            paddingBottom: '20px',
            marginBottom: '40px',
            textAlign: 'left',
          }}>
            <h1 className="contact-title" style={{ fontSize: '3rem', fontWeight: 600, fontFamily: "'Montserrat', sans-serif", color: '#1a1a1a', marginBottom: '8px', textAlign: 'left' }}>
              CONTACT
            </h1>
            <p style={{
              fontSize: '1rem',
              fontFamily: "'Montserrat', sans-serif",
              color: '#666',
              maxWidth: '560px',
              lineHeight: 1.6,
              margin: 0,
            }}>
              We'd love to hear from you! Whether you have a question, want to collaborate, or just want to say hello, we're here to help.
            </p>
          </div>
        </div>
      </section>

      <section className="contact-info">
        <div className="contact-info-inner">
          <div className="contact-info-section">
            <h2 className="contact-info-title">USER SUPPORT</h2>
            <p className="contact-info-detail">contact@theyemberzal.com</p>
            {/* <p className="contact-info-detail">+1 (555) 123-4567</p> */}
          </div>
          <div className="contact-divider"></div>
          <div className="contact-info-section">
            <h2 className="contact-info-title">COLLABORATION</h2>
            <p className="contact-info-detail">business@theyemberzal.com</p>
            {/* <p className="contact-info-detail">@theyemberzal.com</p> */}
          </div>
        </div>
      </section>

      <section className="contact-form-section">
        <div className="contact-form-inner">
          <div className="contact-card">
            <form onSubmit={(e) => handleSubmit(e, 'Contact Form')}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Your name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="category" className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select a category</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Customer Support</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="bug">Bug Report</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="message" className="form-label">Your message</label>
                <textarea
                  name="message"
                  value={formData.message || ''}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="form-control"
                  rows="6"
                  required
                />
              </div>
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'SUBMIT'}
              </button>
            </form>
          </div>
        </div>
      </section>
      </div>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />
    </div>
  );
}

export default Contact;